import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import Sidebar from "../Sidebar/Sidebar";
import config from "../../config";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import IVRDashboardCharts from "./IVRDashboardCharts";

const drawerWidth = 270;
const FILTER_STORAGE_KEY = "ivrManualDashboardFilter";
const BUYER_COLORS = ["#2563EB", "#DB2777", "#16A34A", "#D97706", "#7C3AED", "#0891B2"];
const DEFAULT_IS_LIFETIME = true;

const formatNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
};

const formatDate = (date) => {
  if (!date) return "—";
  const d = dayjs(date);
  return d.isValid() ? d.format("DD MMM YYYY") : "—";
};

const formatPercentage = (value) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(2)}%`;
};

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getToday = () => dayjs().format("YYYY-MM-DD");

const getStoredFilter = () => {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed?.isLifetime !== "boolean") {
      return null;
    }
    return {
      isLifetime: parsed.isLifetime,
      date: parsed.date || getToday(),
    };
  } catch {
    return null;
  }
};

const saveStoredFilter = (isLifetime, date) => {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ isLifetime, date }));
  } catch {
  }
};

const sameArray = (a = [], b = []) => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

const emptyArrival = {
  totalArrival: 0,
  handArrival: 0,
  combineArrival: 0,
  byState: [],
  byVariety: [],
  byMandi: [],
};

const emptyPriceChart = {
  dateFrom: null,
  dateTo: null,
  days: 15,
  filters: {
    state: null,
    mandi: null,
    variety: null,
  },
  dates: [],
  arrivalTrend: [],
  series: [],
};

const emptyData = {
  filters: {},
  arrivalSummary: {
    today: emptyArrival,
    yesterday: emptyArrival,
    change: {
      totalArrival: 0,
      handArrival: 0,
      combineArrival: 0,
    },
  },
  buyerWisePurchase: {
    rows: [],
    matrix: [],
  },
  priceArrivalTrends: [],
  priceArrivalTrendChart: emptyPriceChart,
  dailySummary: {
    date: null,
    totalArrival: 0,
    handArrival: 0,
    combineArrival: 0,
    yesterdayArrival: 0,
    arrivalChange: 0,
    arrivalChangePercentage: 0,
    totalMandis: 0,
    totalVarieties: 0,
    totalBuyers: 0,
    submittedMandiCount: 0,
  },
  speedOfArrival: [],
  arrivalVsPurchase: [],
};

const IVRManualDashboardV2 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initialFilter = useMemo(() => getStoredFilter(), []);
  const [date, setDate] = useState(initialFilter?.date || getToday());
  const [pendingDate, setPendingDate] = useState(initialFilter?.date || getToday());
  const [isLifetime, setIsLifetime] = useState(initialFilter ? initialFilter.isLifetime : true);
  const [pendingLifetime, setPendingLifetime] = useState(initialFilter ? initialFilter.isLifetime : true);
  const [dashboard, setDashboard] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedChartVarieties, setSelectedChartVarieties] = useState([]);
  const abortRef = useRef(null);

  useEffect(() => {
    saveStoredFilter(isLifetime, date);
  }, [isLifetime, date]);

  const fetchDashboard = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (isLifetime) {
        params.set("date", getToday());
        params.set("dateType", "lifetime");
      } else {
        params.set("date", date);
        params.set("dateType", "date");
        params.set("days", "15");
      }
      const token = sessionStorage.getItem("token") || "";
      const response = await fetch(
        `${config.apiBaseUrl}/dashboard/v2/ivr-dashboard-manual?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      const json = await response.json();
      if (json?.success === false) {
        throw new Error(json?.message || "Failed to load dashboard");
      }
      if (controller.signal.aborted) {
        return;
      }
      setDashboard({
        ...emptyData,
        ...json,
        filters: {
          ...emptyData.filters,
          ...(json.filters || {}),
        },
        arrivalSummary: {
          ...emptyData.arrivalSummary,
          ...(json.arrivalSummary || {}),
          today: {
            ...emptyArrival,
            ...(json.arrivalSummary?.today || {}),
          },
          yesterday: {
            ...emptyArrival,
            ...(json.arrivalSummary?.yesterday || {}),
          },
          change: {
            ...emptyData.arrivalSummary.change,
            ...(json.arrivalSummary?.change || {}),
          },
        },
        buyerWisePurchase: {
          ...emptyData.buyerWisePurchase,
          ...(json.buyerWisePurchase || {}),
          rows: Array.isArray(json.buyerWisePurchase?.rows) ? json.buyerWisePurchase.rows : [],
          matrix: Array.isArray(json.buyerWisePurchase?.matrix)
            ? json.buyerWisePurchase.matrix
            : [],
        },
        dailySummary: {
          ...emptyData.dailySummary,
          ...(json.dailySummary || {}),
        },
        priceArrivalTrends: Array.isArray(json.priceArrivalTrends) ? json.priceArrivalTrends : [],
        priceArrivalTrendChart: {
          ...emptyPriceChart,
          ...(json.priceArrivalTrendChart || {}),
        },
        speedOfArrival: Array.isArray(json.speedOfArrival) ? json.speedOfArrival : [],
        arrivalVsPurchase: Array.isArray(json.arrivalVsPurchase) ? json.arrivalVsPurchase : [],
      });
    } catch (err) {
      if (err?.name === "AbortError") {
        return;
      }
      // console.error("IVR Dashboard Error:", err);
      setError(err?.message || "Unable to load dashboard.");
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [date, isLifetime]);

  useEffect(() => {
    fetchDashboard();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchDashboard]);

  const applyFilter = () => {
    setIsLifetime(pendingLifetime);
    setDate(pendingDate);
  };

  const handleToday = () => {
    const today = getToday();
    setPendingDate(today);
    setPendingLifetime(false);
    setIsLifetime(false);
    setDate(today);
  };

  const handleClearFilter = () => {
    const today = getToday();
    setPendingDate(today);
    setPendingLifetime(DEFAULT_IS_LIFETIME);
    setIsLifetime(DEFAULT_IS_LIFETIME);
    setDate(today);
  };

  const handleLifetime = (_event, value) => {
    if (value === null) {
      return;
    }
    setPendingLifetime(value === "lifetime");
  };

  const hasPendingChange = pendingLifetime !== isLifetime || (!pendingLifetime && pendingDate !== date);
  const hasAppliedFilter = isLifetime !== DEFAULT_IS_LIFETIME || (!isLifetime && date !== getToday());

  const selectedDayArrival = dashboard.arrivalSummary?.today || emptyArrival;
  const previousDayArrival = dashboard.arrivalSummary?.yesterday || emptyArrival;
  const priceChart = dashboard.priceArrivalTrendChart || emptyPriceChart;
  const chartSeries = useMemo(() => {
    if (Array.isArray(priceChart.series) && priceChart.series.length) {
      return priceChart.series;
    }
    const map = {};
    (dashboard.priceArrivalTrends || []).forEach((item) => {
      const variety = String(item?.variety || "Unknown").trim();
      if (!map[variety]) {
        map[variety] = {
          variety,
          totalArrival: 0,
          data: [],
        };
      }
      map[variety].data.push({
        date: item.date,
        variety,
        arrival: Number(item.arrival || 0),
        handArrival: Number(item.handArrival || 0),
        combineArrival: Number(item.combineArrival || 0),
        priceMin: Number(item.priceMin || 0) > 0 ? Number(item.priceMin) : null,
        priceMax: Number(item.priceMax || 0) > 0 ? Number(item.priceMax) : null,
        modalPrice: Number(item.modalPrice || 0) > 0 ? Number(item.modalPrice) : null,
      });
      map[variety].totalArrival += Number(item.arrival || 0);
    });
    return Object.values(map);
  }, [priceChart.series, dashboard.priceArrivalTrends]);

  const sortedChartSeries = useMemo(() => {
    return [...chartSeries].sort(
      (a, b) => Number(b.totalArrival || 0) - Number(a.totalArrival || 0)
    );
  }, [chartSeries]);

  useEffect(() => {
    if (!sortedChartSeries.length) {
      setSelectedChartVarieties((previous) => (previous.length ? [] : previous));
      return;
    }
    const available = sortedChartSeries.map((item) => item.variety);
    setSelectedChartVarieties((previous) => {
      const valid = previous.filter((value) => available.includes(value));
      if (previous.length === 0 || valid.length === 0) {
        return available.slice(0, 5);
      }
      const limited = valid.slice(0, 5);
      return sameArray(previous, limited) ? previous : limited;
    });
  }, [sortedChartSeries]);

  const buyerPurchaseSummary = useMemo(() => {
    const rows = dashboard.buyerWisePurchase?.rows || [];
    const totals = new Map();
    rows.forEach((row) => {
      const name = String(row?.buyerName || "Unknown").trim() || "Unknown";
      const quantity = Number(row?.purchaseQuantity || 0);
      totals.set(name, (totals.get(name) || 0) + quantity);
    });
    const list = Array.from(totals.entries())
      .map(([buyerName, quantity]) => ({ buyerName, quantity }))
      .sort((a, b) => b.quantity - a.quantity);
    const total = list.reduce((sum, item) => sum + item.quantity, 0);
    return { list, total };
  }, [dashboard.buyerWisePurchase]);

  const daily = dashboard.dailySummary || {};
  const isNegativeChange = Number(daily.arrivalChange || 0) < 0;

  const activeFilters = useMemo(() => {
    const filters = dashboard.filters || {};
    const result = [];
    if (filters.state) {
      result.push(`State: ${filters.state}`);
    }
    if (filters.mandi) {
      result.push(`Mandi: ${filters.mandi}`);
    }
    if (filters.variety) {
      result.push(`Variety: ${filters.variety}`);
    }
    return result;
  }, [dashboard.filters]);

  const filterQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("dateType", isLifetime ? "lifetime" : "date");
    if (!isLifetime) {
      params.set("date", date);
    }
    return params.toString();
  }, [isLifetime, date]);

  const summaryCards = [
    {
      label: "Selected Date Arrival",
      value: formatNumber(daily.totalArrival),
      sub: formatDate(dashboard.filters?.date || date),
      icon: Inventory2RoundedIcon,
      color: "#2563EB",
      bg: "rgba(37,99,235,0.10)",
    },
    {
      label: "Hand Arrival",
      value: formatNumber(daily.handArrival),
      sub: "Manual / Hand",
      icon: Inventory2RoundedIcon,
      color: "#7C3AED",
      bg: "rgba(124,58,237,0.10)",
    },
    {
      label: "Combine Arrival",
      value: formatNumber(daily.combineArrival),
      sub: "Combine",
      icon: WidgetsRoundedIcon,
      color: "#D97706",
      bg: "rgba(217,119,6,0.10)",
    },
    {
      label: "Total Mandis",
      value: formatNumber(daily.totalMandis),
      sub: "Reported Mandis",
      icon: StorefrontRoundedIcon,
      color: "#16A34A",
      bg: "rgba(22,163,74,0.10)",
      onClick: () => navigate(`/report-by-filter-type/mandi?${filterQueryParams}`),
    },
    {
      label: "Varieties",
      value: formatNumber(daily.totalVarieties),
      sub: "Reported Varieties",
      icon: CategoryRoundedIcon,
      color: "#0891B2",
      bg: "rgba(8,145,178,0.10)",
      onClick: () => navigate(`/report-by-filter-type/variety?${filterQueryParams}`),
    },
    {
      label: "Buyers",
      value: formatNumber(daily.totalBuyers),
      sub: "Total Buyers",
      icon: GroupsRoundedIcon,
      color: "#DB2777",
      bg: "rgba(219,39,119,0.10)",
      onClick: () => navigate(`/report-by-filter-type/buyer?${filterQueryParams}`),
    },
    {
      label: "Submitted",
      value: formatNumber(daily.submittedMandiCount),
      sub: "Mandi Reports",
      icon: ShoppingCartRoundedIcon,
      color: "#059669",
      bg: "rgba(5,150,105,0.10)",
    },
  ];

  const sectionCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(15,23,42,0.06)",
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
    overflow: "hidden",
  };
  const tableHeadSx = {
    fontWeight: 800,
    fontSize: "0.74rem",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#475569",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.08)",
    whiteSpace: "nowrap",
  };
  const tableCellSx = {
    fontSize: "0.82rem",
    borderBottom: "1px solid rgba(15,23,42,0.07)",
    whiteSpace: "nowrap",
  };
  const toggleGroupSx = {
    background: "#fff",
    borderRadius: "10px",
    p: 0.4,
    border: "1px solid rgba(15,23,42,0.08)",
    "& .MuiToggleButton-root": {
      border: "none",
      borderRadius: "8px !important",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.78rem",
      color: "rgba(15,23,42,0.55)",
      px: 1.4,
      gap: 0.6,
    },
    "& .Mui-selected": {
      background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%) !important",
      color: "#fff !important",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        background: "#F8FAFC",
      }}
    >
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            md: `calc(100% - ${drawerWidth}px)`,
          },
          p: {
            xs: 1.5,
            sm: 2.5,
            md: 4,
          },
          overflowX: "hidden",
        }}
      >
        {}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          rowGap={1.5}
          sx={{
            mb: {
              xs: 2,
              md: 3,
            },
          }}
        >
          {isMobile && (
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "12px",
              }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}
          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
            }}
          >
            <DashboardRoundedIcon
              sx={{
                fontSize: 27,
              }}
            />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800}>
              IVR Manual Dashboard
            </Typography>
            <Typography
              sx={{
                color: "rgba(15,23,42,0.55)",
                fontSize: "0.85rem",
              }}
            >
              Arrival, buyer purchase, price and mandi analytics
              {" · "}
              {isLifetime ? "Lifetime" : formatDate(date)}
            </Typography>
          </Box>
          <ToggleButtonGroup value="analytics" exclusive size="small" sx={toggleGroupSx}>
            <ToggleButton value="table" onClick={() => navigate("/ivr-dashboard")}>
              <ListAltRoundedIcon
                sx={{
                  fontSize: 17,
                }}
              />
              {!isXs && "Table"}
            </ToggleButton>
            <ToggleButton value="analytics">
              <InsightsRoundedIcon
                sx={{
                  fontSize: 17,
                }}
              />
              {!isXs && "Analytics"}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {}
        <Card
          elevation={0}
          sx={{
            ...sectionCardSx,
            p: {
              xs: 1.5,
              sm: 2,
            },
            mb: 3,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{
                flexGrow: 1,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "11px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                }}
              >
                <CalendarMonthRoundedIcon
                  sx={{
                    fontSize: 20,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.85rem",
                  }}
                >
                  Dashboard Filter
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(15,23,42,0.5)",
                  }}
                >
                  Select reporting period
                </Typography>
              </Box>
            </Stack>
            <ToggleButtonGroup
              value={pendingLifetime ? "lifetime" : "date"}
              exclusive
              size="small"
              onChange={handleLifetime}
              sx={toggleGroupSx}
            >
              <ToggleButton value="date">
                <TodayRoundedIcon
                  sx={{
                    fontSize: 17,
                  }}
                />
                {!isXs && "By Date"}
              </ToggleButton>
              <ToggleButton value="lifetime">
                <AllInclusiveRoundedIcon
                  sx={{
                    fontSize: 17,
                  }}
                />
                {!isXs && "Lifetime"}
              </ToggleButton>
            </ToggleButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={pendingDate ? dayjs(pendingDate) : null}
                onChange={(value) => {
                  if (!value) {
                    return;
                  }
                  setPendingDate(value.format("YYYY-MM-DD"));
                  setPendingLifetime(false);
                }}
                disabled={pendingLifetime}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    size: "small",
                  },
                }}
                sx={{
                  width: {
                    xs: "100%",
                    sm: 180,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </LocalizationProvider>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasAppliedFilter && !hasPendingChange}
                onClick={handleClearFilter}
                startIcon={<ClearRoundedIcon sx={{ fontSize: 15 }} />}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "rgba(220,38,38,0.3)",
                  color: "#B91C1C",
                  whiteSpace: "nowrap",
                  "&:hover": { borderColor: "#DC2626", background: "rgba(220,38,38,0.04)" },
                }}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToday}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Today
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={applyFilter}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                }}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Card>
        {activeFilters.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            sx={{
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "rgba(15,23,42,0.5)",
                alignSelf: "center",
              }}
            >
              Active filters:
            </Typography>
            {activeFilters.map((item) => (
              <Chip
                key={item}
                size="small"
                label={item}
                sx={{
                  fontWeight: 700,
                }}
              />
            ))}
          </Stack>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: "12px",
            }}
          >
            {error}
          </Alert>
        )}
        {}
        <Grid
          container
          spacing={2}
          sx={{
            mb: 3,
          }}
        >
          {loading
            ? Array.from({
                length: summaryCards.length,
              }).map((_, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Skeleton
                    variant="rounded"
                    height={105}
                    sx={{
                      borderRadius: "16px",
                    }}
                  />
                </Grid>
              ))
            : summaryCards.map((card) => {
                const Icon = card.icon;
                const clickable = !!card.onClick;
                return (
                  <Grid item xs={6} sm={4} md={3} key={card.label}>
                    <Card
                      elevation={0}
                      onClick={card.onClick}
                      sx={{
                        ...sectionCardSx,
                        p: {
                          xs: 1.5,
                          md: 2,
                        },
                        height: "100%",
                        cursor: clickable ? "pointer" : "default",
                        transition: "0.2s",
                        "&:hover": clickable
                          ? {
                              transform: "translateY(-2px)",
                              boxShadow: "0 10px 24px rgba(15,23,42,0.10)",
                            }
                          : undefined,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: {
                              xs: 42,
                              md: 52,
                            },
                            height: {
                              xs: 42,
                              md: 52,
                            },
                            flexShrink: 0,
                            borderRadius: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: card.bg,
                            color: card.color,
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: {
                                xs: 21,
                                md: 26,
                              },
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              color: "rgba(15,23,42,0.55)",
                              fontWeight: 600,
                              fontSize: "0.76rem",
                            }}
                          >
                            {card.label}
                          </Typography>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: {
                                xs: "1.15rem",
                                md: "1.45rem",
                              },
                              fontWeight: 800,
                            }}
                          >
                            {card.value}
                          </Typography>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              color: card.color,
                            }}
                          >
                            {card.sub}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
        </Grid>
        {}
        <Card
          elevation={0}
          sx={{
            ...sectionCardSx,
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(15,23,42,0.07)",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingUpRoundedIcon
                  sx={{
                    color: "#2563EB",
                  }}
                />
                <Typography fontWeight={800}>Arrival Comparison</Typography>
              </Stack>
              <Chip
                size="small"
                icon={isNegativeChange ? <TrendingDownRoundedIcon /> : <TrendingUpRoundedIcon />}
                label={formatPercentage(daily.arrivalChangePercentage)}
                sx={{
                  fontWeight: 700,
                  background: isNegativeChange ? "rgba(220,38,38,0.10)" : "rgba(22,163,74,0.10)",
                  color: isNegativeChange ? "#B91C1C" : "#166534",
                }}
              />
            </Stack>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadSx}>Metric</TableCell>
                  <TableCell sx={tableHeadSx} align="right">
                    Selected Date
                  </TableCell>
                  <TableCell sx={tableHeadSx} align="right">
                    Previous Day
                  </TableCell>
                  <TableCell sx={tableHeadSx} align="right">
                    Change
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  [
                    "Total Arrival",
                    selectedDayArrival.totalArrival,
                    previousDayArrival.totalArrival,
                    dashboard.arrivalSummary?.change?.totalArrival,
                  ],
                  [
                    "Hand Arrival",
                    selectedDayArrival.handArrival,
                    previousDayArrival.handArrival,
                    dashboard.arrivalSummary?.change?.handArrival,
                  ],
                  [
                    "Combine Arrival",
                    selectedDayArrival.combineArrival,
                    previousDayArrival.combineArrival,
                    dashboard.arrivalSummary?.change?.combineArrival,
                  ],
                ].map((row) => {
                  const change = Number(row[3]) || 0;
                  return (
                    <TableRow hover key={row[0]}>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontWeight: 700,
                        }}
                      >
                        {row[0]}
                      </TableCell>
                      <TableCell sx={tableCellSx} align="right">
                        {formatNumber(row[1])}
                      </TableCell>
                      <TableCell sx={tableCellSx} align="right">
                        {formatNumber(row[2])}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontWeight: 800,
                          color: change >= 0 ? "#15803D" : "#DC2626",
                        }}
                        align="right"
                      >
                        {change > 0 ? "+" : ""}
                        {formatNumber(change)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {}
        <Grid
          container
          spacing={2}
          sx={{
            mb: 3,
          }}
        >
          {}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={sectionCardSx}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(15,23,42,0.07)",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StorefrontRoundedIcon
                      sx={{
                        color: "#16A34A",
                      }}
                    />
                    <Typography fontWeight={800}>Arrival by State</Typography>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => navigate(`/report-by-filter-type/state?${filterQueryParams}`)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    View Reports
                  </Button>
                </Stack>
              </Box>
              <TableContainer
                sx={{
                  maxHeight: 420,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadSx}>State</TableCell>
                      <TableCell sx={tableHeadSx} align="right">
                        Total
                      </TableCell>
                      <TableCell sx={tableHeadSx} align="right">
                        Hand
                      </TableCell>
                      <TableCell sx={tableHeadSx} align="right">
                        Combine
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedDayArrival.byState || []).map((item, index) => (
                      <TableRow hover key={`${item.state}-${index}`}>
                        <TableCell
                          sx={{
                            ...tableCellSx,
                            fontWeight: 700,
                          }}
                        >
                          {item.state}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(item.totalArrival)}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(item.handArrival)}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(item.combineArrival)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!selectedDayArrival.byState?.length && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{
                            py: 5,
                            color: "text.secondary",
                          }}
                        >
                          No state arrival data for selected date
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
          {}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={sectionCardSx}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(15,23,42,0.07)",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CategoryRoundedIcon
                      sx={{
                        color: "#2563EB",
                      }}
                    />
                    <Typography fontWeight={800}>Arrival by Variety</Typography>
                  </Stack>
                  <Button
                    size="small"
                    onClick={() => navigate(`/report-by-filter-type/variety?${filterQueryParams}`)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    View Reports
                  </Button>
                </Stack>
              </Box>
              <TableContainer
                sx={{
                  maxHeight: 420,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadSx}>Variety</TableCell>
                      <TableCell sx={tableHeadSx} align="right">
                        Total
                      </TableCell>
                      <TableCell sx={tableHeadSx} align="right">
                        Hand
                      </TableCell>
                      <TableCell sx={tableHeadSx} align="right">
                        Combine
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedDayArrival.byVariety || []).map((item, index) => (
                      <TableRow hover key={`${item.variety}-${index}`}>
                        <TableCell
                          sx={{
                            ...tableCellSx,
                            fontWeight: 700,
                          }}
                        >
                          <Chip
                            size="small"
                            label={item.variety}
                            sx={{
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(item.totalArrival)}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(item.handArrival)}
                        </TableCell>
                        <TableCell sx={tableCellSx} align="right">
                          {formatNumber(item.combineArrival)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!selectedDayArrival.byVariety?.length && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{
                            py: 5,
                            color: "text.secondary",
                          }}
                        >
                          No variety arrival data for selected date
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
        {}
        <Card
          elevation={0}
          sx={{
            ...sectionCardSx,
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(15,23,42,0.07)",
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "flex-start",
                sm: "center",
              }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <ShoppingCartRoundedIcon
                  sx={{
                    color: "#DB2777",
                  }}
                />
                <Typography fontWeight={800}>Buyer-wise Purchase</Typography>
                {!!buyerPurchaseSummary.list.length && (
                  <Chip
                    size="small"
                    label={`${buyerPurchaseSummary.list.length} buyers`}
                    sx={{
                      fontWeight: 700,
                      background: "rgba(219,39,119,0.10)",
                      color: "#9D174D",
                    }}
                  />
                )}
              </Stack>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/report-by-buyer-wise/buyer?${filterQueryParams}`)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                View Reports
              </Button>
            </Stack>
          </Box>
          <TableContainer
            sx={{
              maxHeight: 480,
            }}
          >
            {buyerPurchaseSummary.list.length ? (
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableHeadSx, width: "56%" }}>Buyer</TableCell>
                    <TableCell sx={tableHeadSx} align="right">
                      Purchase Quantity
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {buyerPurchaseSummary.list.map((buyer, index) => {
                    const share = buyerPurchaseSummary.total
                      ? (buyer.quantity / buyerPurchaseSummary.total) * 100
                      : 0;
                    const color = BUYER_COLORS[index % BUYER_COLORS.length];
                    return (
                      <TableRow hover key={buyer.buyerName}>
                        <TableCell
                          sx={{
                            ...tableCellSx,
                            whiteSpace: "normal",
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                flexShrink: 0,
                                borderRadius: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.72rem",
                                fontWeight: 800,
                                color,
                                background: `${color}1A`,
                              }}
                            >
                              {getInitials(buyer.buyerName)}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                noWrap
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.82rem",
                                }}
                              >
                                {buyer.buyerName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.68rem",
                                  color: "rgba(15,23,42,0.5)",
                                  fontWeight: 600,
                                }}
                              >
                                {formatPercentage(share)} of total
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ ...tableCellSx, minWidth: 160 }} align="right">
                          <Stack spacing={0.5} alignItems="flex-end">
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: "0.88rem",
                              }}
                            >
                              {formatNumber(buyer.quantity)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(share, 100)}
                              sx={{
                                width: 120,
                                height: 6,
                                borderRadius: 3,
                                background: "rgba(15,23,42,0.06)",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 3,
                                  background: color,
                                },
                              }}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  minHeight: 180,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 1,
                  color: "text.secondary",
                }}
              >
                <ShoppingCartRoundedIcon
                  sx={{
                    fontSize: 42,
                    opacity: 0.25,
                  }}
                />
                <Typography fontWeight={700}>No buyer purchase data</Typography>
              </Box>
            )}
          </TableContainer>
          {!!buyerPurchaseSummary.list.length && (
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderTop: "1px solid rgba(15,23,42,0.07)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: "0.74rem", color: "rgba(15,23,42,0.5)", fontWeight: 700 }}>
                Total Purchase
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "#9D174D" }}>
                {formatNumber(buyerPurchaseSummary.total)}
              </Typography>
            </Box>
          )}
        </Card>
        <IVRDashboardCharts
          series={priceChart.series || []}
          fallbackRows={dashboard.priceArrivalTrends || []}
          selectedVarieties={selectedChartVarieties}
          onSelectionChange={setSelectedChartVarieties}
          dateFrom={priceChart.dateFrom}
          dateTo={priceChart.dateTo}
          days={priceChart.days || 15}
          maxSelections={5}
        />
        <Typography
          sx={{
            textAlign: "center",
            color: "rgba(15,23,42,0.4)",
            fontSize: "0.72rem",
            py: 2,
          }}
        >
          IVR Manual Dashboard · Data based on submitted mandi reports
        </Typography>
      </Box>
    </Box>
  );
};

export default IVRManualDashboardV2;